import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useLeadId } from 'src/common/lead-id'
import { buildQueryString } from 'src/common/utils'
import Section from 'src/components/shared/Section'
import Typography from 'src/components/shared/Typography'
import { useAnalytics } from 'src/common/analytics'
import { numberFormat } from 'src/common/utils'
import { TrustPilot } from 'src/components/shared/TrustMarkerWidgets'
import PrimaryButton from 'src/components/shared/PrimaryButton'
import { APPLY_FDR_URL } from 'src/common/constants'
import { ScheduleACallCTA } from 'src/components/shared/ScheduleACallCTA'

import HeroCarousel from 'src/components/shared/BubbleCarousel'

const formatOpt = {
  style: 'currency',
  currency: 'USD',
}

const HeroSection = ({
  showNewTrustpilot,
  trustPilotInfo,
  phone,
  btnName,
  phoneButtonColor,
  phoneButtonTextColor,
  showScheduleACallButton,
}) => {
  const router = useRouter()
  const { track } = useAnalytics()

  const onButtonClick = (e) => {
    e.preventDefault()

    const data = {
      click_id: 'Learn how it works',
      click_text: 'FDR-Web | Learn how it works',
      click_type: 'Button Click',
      click_url: '/how-it-works/',
      event_action: 'click',
      event_type: 'track',
    }

    if (typeof track === 'function') {
      track(data, {}, 'button_click')
    }

    router.push('/how-it-works')
  }

  const leadId = useLeadId()
  const { query: baseQuery } = useRouter()

  let query = useMemo(
    () => buildQueryString(baseQuery, leadId),
    [baseQuery, leadId]
  )

  const handlePreviewClick = () => {
    track(
      {
        event_type: 'track',
        click_id: 'Trustpilot',
        click_type: 'Link Click',
        nav_link_section: 'Hero Card',
        additional_info: {
          section: 'trust-marker-truspilot',
          click_url: trustPilotInfo?.link || '',
        },
      },
      {},
      'button_click'
    )
  }

  return (
    <Section>
      <div className="lg:flex lg:items-center lg:justify-center lg:gap-x-9">
        <div
          className="lg:basis-132 text-center lg:text-left"
          data-testid="facts-hero-content"
        >
          <Typography variant="labelOverline" component="h1">
            FREEDOM DEBT RELIEF FACTS
          </Typography>
          <Typography
            variant="displayLarge"
            lg={{ variant: 'display3XLarge' }}
            component="h2"
            mt={4}
            mb={4}
          >
            Get the facts about Freedom Debt Relief
          </Typography>
          <Typography variant="bodyMedium" lg={{ variant: 'bodyLarge' }}>
            More than 1 million people have turned to Freedom Debt Relief for
            help resolving their debt for less than they owe.
          </Typography>

          <div
            id="heroPrimaryBtnContent"
            className="mt-4 flex w-full flex-col items-center self-center text-center lg:w-fit lg:flex-row lg:items-start lg:gap-6 lg:self-start "
            data-testid="facts-hero-cta-wrapper"
          >
            <div className="flex gap-4">
              <div className="flex flex-col  justify-center">
                <PrimaryButton
                  id="heroPrimaryButton"
                  heroBtn={true}
                  sectionTitle={btnName}
                  title={btnName}
                  href={`${APPLY_FDR_URL()}?${query}`}
                  className="w-[278px] border-2 border-red-850 bg-red-850 px-4.5 py-2.5 text-lg font-bold text-white lg:px-6"
                  dataTestId="facts-hero-primary-button"
                />
                {showScheduleACallButton && (
                  <ScheduleACallCTA
                    nav_link_section="Schedule a call component"
                    page_section="Hero Card"
                    variant="outlined"
                    className="mt-3 block h-13 w-full px-11 text-lg lg:hidden"
                  >
                    Schedule a call
                  </ScheduleACallCTA>
                )}
                {showNewTrustpilot ? (
                  <div className="hidden lg:block">
                    <TrustPilot
                      {...trustPilotInfo}
                      loading="eager"
                      onReviewClick={handlePreviewClick}
                    />
                  </div>
                ) : null}
              </div>
              {showScheduleACallButton && (
                <ScheduleACallCTA
                  nav_link_section="Schedule a call component"
                  page_section="Hero Card"
                  variant="outlined"
                  className="hidden h-13 px-11 text-lg lg:block"
                >
                  Schedule a call
                </ScheduleACallCTA>
              )}
            </div>

            {phone && !showScheduleACallButton ? (
              <PrimaryButton
                id="heroPhoneButton"
                sectionTitle={'TITLE 2'}
                className={`max-h-13 mt-3 border-2 ${
                  phoneButtonColor ? phoneButtonColor : 'border-blue-base'
                } w-[278px] px-6 py-2.5 text-lg font-bold lg:w-[230px] ${
                  phoneButtonTextColor ? phoneButtonTextColor : 'text-blue-base'
                } lg:mt-0`}
                title={`Call ${phone}`}
                href={`tel:${phone}`}
                heroBtn
              />
            ) : null}

            {showNewTrustpilot ? (
              <div className="mt-3 lg:mt-4 lg:hidden">
                <TrustPilot
                  {...trustPilotInfo}
                  loading="eager"
                  onReviewClick={handlePreviewClick}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="lg:basis-132" data-testid="facts-hero-media">
          <HeroCarousel
            items={[
              {
                src: '/next-assets/facts/hero/molly-1.webp',
                alt: 'This is an image. Extracted text content: Resolved $29,987 in debt. Paid off 6 enrolled debts! 1 monthly payment of $562. Molly B., Los Angeles, CA.',
                content: {
                  resolvedDebtAmount: numberFormat(29987, formatOpt),
                  paidOffDebtsCount: 6,
                  monthlyPaymentCount: 1,
                  monthlyPaymentAmount: numberFormat(562, formatOpt),
                  place: 'Los Angeles, CA',
                  name: 'Molly B.',
                },
              },
              {
                src: '/next-assets/facts/hero/ozzy-1.webp',
                alt: 'This is an image. Extracted text content: Resolved $22,738 in debt. Paid off 8 enrolled debts! 1 monthly payment of $398. Ozzy S.',
                content: {
                  resolvedDebtAmount: numberFormat(22738, formatOpt),
                  paidOffDebtsCount: 8,
                  monthlyPaymentCount: 1,
                  monthlyPaymentAmount: numberFormat(398, formatOpt),
                  place: 'Staten Island, NY',
                  name: 'Ozzy S.',
                },
              },
              {
                src: '/next-assets/facts/hero/patti-1.webp',
                alt: 'This is an image. Extracted text content: Resolved $17,183 in debt. Paid off 4 enrolled debts! 1 monthly payment of $362. Patti B.',
                content: {
                  resolvedDebtAmount: numberFormat(17183, formatOpt),
                  paidOffDebtsCount: 4,
                  monthlyPaymentCount: 1,
                  monthlyPaymentAmount: numberFormat(362, formatOpt),
                  place: 'San Francisco, CA',
                  name: 'Patti B.',
                },
              },
            ]}
          />
          <p className="mt-[34px] text-center text-12 font-normal leading-[16.8px] text-neutral-300 lg:mt-3 lg:text-left">
            Actual clients of Freedom Debt Relief. Individual results will vary.
          </p>
        </div>
      </div>
    </Section>
  )
}

export default HeroSection
